import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Button } from "../../../components/ui/button.tsx";
import { Input } from "../../../components/ui/input.tsx";
import { useSelector } from "react-redux";
import { API_BASE_URL } from "../../../config/pwaConfig.ts";
import { Pencil, Trash2, Plus, X, Check, Tag } from "lucide-react";

interface OrderStatus {
  id: number;
  label: string;
  color: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

const defaultColor = "#6B7280";
const presetColors = [
  "#2563EB",
  "#16A34A",
  "#EA580C",
  "#DC2626",
  "#7C3AED",
  "#0891B2",
  "#D97706",
  "#475569",
];

const emptyForm = { label: "", color: defaultColor, sortOrder: 0 };

export const QuotationStatusPage = () => {
  const { authToken = "", id = "" } = useSelector(
    (state: any) => state.loginData || {}
  );

  const [statuses, setStatuses] = useState<OrderStatus[]>([]);
  const [loading, setLoading] = useState(false);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  // Tag input — new tag being typed before adding
  const [tagInput, setTagInput] = useState("");

  const fetchStatuses = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}account/${id}/order-statuses`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setStatuses(res.data.data || []);
    } catch {
      toast.error("Failed to fetch quotation statuses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchStatuses();
    // eslint-disable-next-line
  }, [id]);

  const openAddForm = () => {
    setEditId(null);
    setForm(emptyForm);
    setTagInput("");
    setShowForm(true);
  };

  const openEditForm = (s: OrderStatus) => {
    setEditId(s.id);
    setForm({ label: s.label, color: s.color, sortOrder: s.sortOrder });
    setTagInput(s.label);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditId(null);
    setForm(emptyForm);
    setTagInput("");
  };

  // Sync tag input into form label
  const handleTagInputChange = (val: string) => {
    setTagInput(val);
    setForm((f) => ({ ...f, label: val }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.label.trim()) {
      toast.error("Label is required");
      return;
    }
    setSaving(true);
    try {
      if (editId !== null) {
        await axios.put(
          `${API_BASE_URL}account/${id}/order-status/update`,
          { id: editId, label: form.label.trim(), color: form.color, sortOrder: form.sortOrder },
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        toast.success("Status updated");
      } else {
        await axios.post(
          `${API_BASE_URL}account/${id}/order-status/create`,
          { label: form.label.trim(), color: form.color, sortOrder: form.sortOrder },
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
        toast.success("Status added");
      }
      closeForm();
      fetchStatuses();
    } catch {
      toast.error(editId !== null ? "Failed to update status" : "Failed to add status");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (statusId: number, label: string) => {
    if (!window.confirm(`Delete status "${label}"?`)) return;
    try {
      await axios.delete(
        `${API_BASE_URL}account/${id}/order-status/${statusId}/delete`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      toast.success("Status deleted");
      fetchStatuses();
    } catch {
      toast.error("Failed to delete status");
    }
  };

  return (
    <div className="px-3 py-4 md:p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-2 sm:items-center">
          <Tag className="w-6 h-6 text-gray-600" />
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-800">Quotation Status Tags</h1>
        </div>
        <Button
          onClick={openAddForm}
          className="flex w-full items-center justify-center gap-2 bg-blue-600 text-white shadow-sm transition-colors hover:bg-blue-700 sm:w-auto"
        >
          <Plus className="w-4 h-4" />
          Add Tag
        </Button>
      </div>

      {/* Inline Add / Edit Form */}
      {showForm && (
        <div className="mb-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
          <h2 className="text-base font-medium text-gray-700 mb-4">
            {editId !== null ? "Edit Status Tag" : "New Status Tag"}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Tag input */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Tag Label <span className="text-red-500">*</span>
              </label>
              <Input
                value={tagInput}
                onChange={(e) => handleTagInputChange(e.target.value)}
                placeholder="e.g. Ordered, Delivered, Accepted…"
                autoFocus
                disabled={saving}
              />
              {/* Preview badge */}
              {tagInput.trim() && (
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-xs text-gray-500">Preview:</span>
                  <span
                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium text-white"
                    style={{ backgroundColor: form.color }}
                  >
                    {tagInput.trim()}
                  </span>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1fr)_180px]">
              {/* Color picker */}
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-600 mb-1">Color</label>
                <div className="flex flex-wrap items-center gap-3">
                  <input
                    type="color"
                    value={form.color}
                    onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                    className="w-10 h-10 rounded cursor-pointer border border-gray-300 p-0.5"
                    disabled={saving}
                  />
                  <Input
                    value={form.color}
                    onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                    className="w-32 font-mono text-sm"
                    placeholder="#6B7280"
                    disabled={saving}
                  />
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {presetColors.map((color) => {
                    const selected = form.color.toLowerCase() === color.toLowerCase();
                    return (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, color }))}
                        disabled={saving}
                        className={`h-9 w-9 rounded-full border-2 transition-all ${selected ? "scale-110 border-gray-900 shadow-md" : "border-white shadow-sm hover:scale-105"}`}
                        style={{ backgroundColor: color }}
                        title={color}
                      />
                    );
                  })}
                </div>
              </div>

              {/* Sort order */}
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-600 mb-1">Sort Order</label>
                <Input
                  type="number"
                  value={form.sortOrder}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, sortOrder: parseInt(e.target.value) || 0 }))
                  }
                  min={0}
                  className="w-full md:w-24"
                  disabled={saving}
                />
              </div>
            </div>

            <div className="flex flex-col gap-2 pt-1 sm:flex-row">
              <Button
                type="submit"
                disabled={saving}
                className="flex items-center justify-center gap-2 bg-blue-600 text-white shadow-sm transition-colors hover:bg-blue-700"
              >
                <Check className="w-4 h-4" />
                {saving ? "Saving…" : editId !== null ? "Update" : "Add Tag"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={closeForm}
                disabled={saving}
                className="flex items-center justify-center gap-2 border-red-200 text-red-600 transition-colors hover:bg-red-50 hover:text-red-700"
              >
                <X className="w-4 h-4" />
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Tags list */}
      {loading ? (
        <div className="text-center text-gray-500 py-12">Loading…</div>
      ) : statuses.length === 0 ? (
        <div className="text-center text-gray-400 py-12">
          No status tags yet. Click "Add Tag" to create one.
        </div>
      ) : (
        <>
          <div className="space-y-3 md:hidden">
            {statuses.map((s) => (
              <div key={s.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <span
                      className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium text-white"
                      style={{ backgroundColor: s.color }}
                    >
                      {s.label}
                    </span>
                    <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
                      <span
                        className="h-4 w-4 rounded-full border border-gray-200"
                        style={{ backgroundColor: s.color }}
                      />
                      <span className="font-mono">{s.color}</span>
                    </div>
                    <div className="mt-2 text-sm text-gray-600">Sort Order: <span className="font-medium text-gray-800">{s.sortOrder}</span></div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openEditForm(s)}
                      className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
                      title="Edit"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(s.id, s.label)}
                      className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="hidden overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm md:block">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-5 py-3 font-medium text-gray-600 w-1/2">Tag</th>
                <th className="text-left px-5 py-3 font-medium text-gray-600">Color</th>
                <th className="text-center px-5 py-3 font-medium text-gray-600">Order</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {statuses.map((s, i) => (
                <tr
                  key={s.id}
                  className={`border-b border-gray-100 last:border-0 ${
                    i % 2 === 0 ? "bg-white" : "bg-gray-50/50"
                  }`}
                >
                  <td className="px-5 py-3">
                    <span
                      className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium text-white"
                      style={{ backgroundColor: s.color }}
                    >
                      {s.label}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <span
                        className="w-5 h-5 rounded-full border border-gray-200 flex-shrink-0"
                        style={{ backgroundColor: s.color }}
                      />
                      <span className="text-gray-500 font-mono text-xs">{s.color}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-center text-gray-500">{s.sortOrder}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => openEditForm(s)}
                        className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                        title="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(s.id, s.label)}
                        className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </>
      )}
    </div>
  );
};

export default QuotationStatusPage;
